import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDisclosureStore, useHelperStore, useTabStore } from "~/stores";
import useDisclosures from "~/hooks/useDisclosures";
import useDefaultAllDisclosures from "./useDefaultAllDisclosures";
import { LanguageContext } from "~/providers/LanguageProvider";

import { TSetActiveDisclosure } from "~/stores/disclosure/disclosure.state";
import { NormalizedDisclosure, SEARCH_TABS } from "~/types/search.types";
import { SearchActions } from "~/types/actions.enum";
import { normalizeDisclosureMetaData } from "~/utils/normalize.utils";
import { AdvancedSearchContext, FilterType } from "~/providers/AdvancedSearchProvider";
import useSearchDialogForm from "./useSearchDialogForm";

export type TypeMsg = {
    label: string;
    message: string;
    key?: string;
};

/**
 * Initializes the Disclosures list by fetching and normalizing its data into the `useDisclosureStore()` states.
 */
const useInitDisclosuresList = () => {
    const { disclosures, isLoading } = useDisclosures();
    const { DEFAULT_ALL_DISCLOSURES, allDisclosuresLabel } = useDefaultAllDisclosures();
    const { language } = useContext(LanguageContext);
    const { setDisclosureFilters } = useContext(AdvancedSearchContext);
    const { resetOptions, openModal } = useSearchDialogForm();

    const dispatch = useDisclosureStore(state => state.dispatch);
    const isAppInitialized = useHelperStore(state => state.isAppInitialized);
    const isFetching = useHelperStore(state => state.isLoading);
    const disclosure = useDisclosureStore(state => state.disclosure);

    const [disclosureName, setDisclosureName] = useState("");
    const normalizedDisclosures = useMemo<NormalizedDisclosure[]>(() => {
        const normalized = normalizeDisclosureMetaData(disclosures);
        return normalized;
    }, [disclosures]);
    const activeTab = useTabStore(state => state.tab);
    const isFullDocuments = activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS;
    const location = useLocation();

    const fullDisclosuresList = useMemo<NormalizedDisclosure[]>(() => {
        return isFullDocuments
            ? normalizedDisclosures
            : [DEFAULT_ALL_DISCLOSURES, ...normalizedDisclosures];
    }, [normalizedDisclosures, isFullDocuments]);

    useEffect(() => {
        dispatch({ type: SearchActions.SET_DISCLOSURE_ITEMS, payload: normalizedDisclosures });
    }, [normalizedDisclosures]);

    useEffect(() => {
        if (!disclosure) return;
        const disclosureName = disclosure?.label[language];
        const defaultName = disclosure?.id > -1 ? disclosureName : allDisclosuresLabel;
        setDisclosureName(defaultName);
    }, [disclosure, language]);

    useEffect(() => {
        if (location.pathname === "/search") {
            if (!isFullDocuments) {
                dispatch({
                    type: SearchActions.SET_ACTIVE_DISCLOSURE,
                    payload: DEFAULT_ALL_DISCLOSURES
                });
                setDisclosureName(allDisclosuresLabel);
            } else if (fullDisclosuresList.length > 0) {
                const item = {
                    property: "exploreId",
                    value: fullDisclosuresList[0].exploreId
                } as TSetActiveDisclosure;

                dispatch({ type: SearchActions.SET_ACTIVE_DISCLOSURE_BY_KEY, payload: item });
                setDisclosureName(fullDisclosuresList[0].label[language]);
            }
        }
    }, [activeTab, fullDisclosuresList, isFullDocuments]);

    const handleDisclosureClick = (normalizeDisclosure: NormalizedDisclosure) => {
        const isAllDisclosure = normalizeDisclosure?.id === -1;
        const isSameDisclosure = disclosure?.exploreId === normalizeDisclosure.exploreId;

        if (isAllDisclosure) {
            setDisclosureFilters([]);

            const actionType =
            location.pathname === "/search"
                ? SearchActions.SET_ACTIVE_DISCLOSURE
                : SearchActions.SET_IS_ALL_DISCLOSURES;

            dispatch({
                type: actionType,
                payload: DEFAULT_ALL_DISCLOSURES,
            });
        } else {
            const item: TSetActiveDisclosure = {
                property: "exploreId",
                value: normalizeDisclosure.exploreId,
            };

            const disclosureFilter: FilterType = {
                id: normalizeDisclosure.exploreId,
                name: normalizeDisclosure?.label?.en || "",
                frName: normalizeDisclosure?.label?.fr || "",
            };

            setDisclosureFilters([disclosureFilter]);
            dispatch({ type: SearchActions.SET_ACTIVE_DISCLOSURE_BY_KEY, payload: item });
        }

        if (location.pathname === "/search/result") {
            if (!isAllDisclosure && !isSameDisclosure) {
                resetOptions();
            }
            openModal(true);
        }
    };

    return {
        fullDisclosuresList,
        disclosure,
        isLoading,
        isFetching,
        isAppInitialized,
        language,
        disclosureName,
        activeTab,
        handleDisclosureClick
    };
};

export default useInitDisclosuresList;
